import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api } from '../lib/api.js'
import { useAuth } from '../store/authStore.js'
import { computeProRes, bandFor, SCALE_MIN, SCALE_MAX } from '../lib/score-prores.js'
import { ArrowLeft, Printer } from 'lucide-react'
import opLogo from '../assets/op.png'

export default function ProResResult() {
  const { accessId } = useParams()
  const me = useAuth(s => s.user)
  const [data, setData] = useState(null)
  const [completedAt, setCompletedAt] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.getResponse(accessId).then(r => {
      setData(r?.data || {})
      setCompletedAt(r?.completedAt || null)
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [accessId])

  if (loading) return <div className="text-ink-soft">Lade Ergebnis…</div>
  if (!data) return <div className="text-rose-700">Kein Ergebnis gefunden.</div>

  const profile = computeProRes(data)
  const dateLabel = formatDate(completedAt)
  const overallAvg = profile.overall.average
  const overallBand = bandFor(overallAvg)

  return (
    <div className="space-y-8 max-w-5xl mx-auto print:space-y-0 print:max-w-none print:mx-0">
      {/* Action-Bar */}
      <div className="flex items-center justify-between print:hidden">
        <Link to="/" className="btn-ghost"><ArrowLeft size={18} /> Zurück zur Übersicht</Link>
        <button onClick={() => window.print()} className="btn-secondary"><Printer size={18} /> Drucken</button>
      </div>

      {/* Cover */}
      <header className="card overflow-hidden print:break-after-page">
        <div className="px-8 pt-8 pb-2 flex items-start justify-between gap-6">
          <div className="text-xs font-semibold tracking-widest text-teal-700 uppercase">WIR SEHEN. BUNT.</div>
          <img src={opLogo} alt="O+P Consult" className="h-10 w-auto" />
        </div>
        <div className="px-8 py-6 flex flex-col md:flex-row md:items-end md:justify-between gap-6 print:py-12">
          <div>
            <div className="text-sm uppercase tracking-wider text-ink-soft">Ergebnisbericht · Version 2.5</div>
            <h1 className="text-3xl md:text-4xl font-bold mt-1 leading-tight">ProRes — Profil arbeits&shy;bezogener Ressourcen</h1>
            <p className="text-lg text-ink mt-3">{[me?.firstName, me?.lastName].filter(Boolean).join(' ') || '—'}</p>
          </div>
          <div className="text-xs text-ink-soft md:text-right space-y-1">
            <p className="font-semibold text-ink">O+P Consult GmbH</p>
            <p>Hans-Thoma-Straße 98<br />68163 Mannheim</p>
            <p>
              <a href="mailto:bunt@op-consult.de" className="hover:underline">bunt@op-consult.de</a><br />
              <a href="https://www.op-consult.de" target="_blank" rel="noreferrer" className="hover:underline">www.op-consult.de</a>
            </p>
            {dateLabel && <p className="pt-2">Datum: {dateLabel}</p>}
          </div>
        </div>
        <div className="px-8 py-3 bg-teal-50/40 border-t border-teal-100/60 flex items-center justify-between text-xs text-ink-mute print:bg-transparent print:border-t-0 print:px-0 print:pt-8">
          <span>op-consult.de</span>
          <span>© O+P Consult GmbH</span>
          <span className="italic">Beratung, die bewegt.</span>
        </div>
      </header>

      {/* Einführung */}
      <Section number={1} id="einfuehrung" title="Einführung">
        <div className="space-y-3 text-ink leading-relaxed">
          <p>
            ProRes hilft Ihnen zu erkunden, welche resilienzfördernden Ressourcen Sie bereits nutzen
            und wo Potenzial zum weiteren Ausbau dieser Ressourcen liegt. Erfasst werden 15 Ressourcen
            in zwei Bereichen: <strong>personale</strong> Ressourcen, die in Ihnen selbst verankert sind, und
            {' '}<strong>organisationale</strong> Ressourcen, die Ihr Arbeitsumfeld bereitstellt.
          </p>
          <p>
            Die Ergebnisse sind für Sie persönlich und dienen als Reflexionsgrundlage. Ihr Punktwert
            gibt lediglich ein Bild Ihrer persönlichen Einschätzung wieder; er erlaubt keine vergleichende
            Aussage über verschiedene Personen oder Organisationen hinweg.
          </p>
        </div>
      </Section>

      {/* Gesamtbild */}
      <Section number={2} id="gesamt" title="Gesamtbild">
        <div className="grid sm:grid-cols-3 gap-5 mb-6">
          <Stat label="Beantwortete Items" value={`${profile.overall.answered} von ${profile.overall.total}`} />
          <Stat label="Gesamt-Mittelwert" value={overallAvg != null ? overallAvg.toFixed(1) : '—'} suffix={`/${SCALE_MAX}`} />
          <Stat label="Einordnung" value={overallBand?.label || '—'} />
        </div>
        <p className="text-sm text-ink-soft leading-relaxed">
          Der Gesamt-Mittelwert ist der Durchschnitt der 15 Skalen-Mittelwerte. Auf den folgenden Seiten
          finden Sie die Werte einzeln, gruppiert nach personalen und organisationalen Ressourcen.
        </p>
      </Section>

      {/* Personale Ressourcen */}
      <Section number={3} id="personal" title="Personale Ressourcen">
        <p className="text-sm text-ink-soft mb-6 leading-relaxed">
          Ressourcen, die in Ihnen selbst verankert sind — Selbstwirksamkeit, Lösungsorientierung,
          Selbstfürsorge und mehr.
        </p>
        <ScaleList scales={profile.personal} />
      </Section>

      {/* Organisationale Ressourcen */}
      <Section number={4} id="organisational" title="Organisationale Ressourcen">
        <p className="text-sm text-ink-soft mb-6 leading-relaxed">
          Ressourcen, die Ihr Arbeitsumfeld bereitstellt — Aufgabe, Unterstützung, Autonomie,
          Feedback und mehr.
        </p>
        <ScaleList scales={profile.organisational} />
      </Section>

      {/* Reflexion */}
      <Section number={5} id="reflexion" title="Anregungen zur Selbstreflexion">
        <ul className="space-y-3 text-ink leading-relaxed">
          {REFLECTION_PROMPTS.map((q, i) => (
            <li key={i} className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-teal-100 text-teal-700 grid place-items-center text-xs font-semibold mt-0.5">{i + 1}</span>
              <span>{q}</span>
            </li>
          ))}
        </ul>
      </Section>
    </div>
  )
}

const REFLECTION_PROMPTS = [
  'Welche Ihrer personalen Ressourcen sind besonders ausgeprägt — wie haben Sie diese im Berufsleben aufgebaut?',
  'Wo sehen Sie das größte Potenzial, eine personale Ressource gezielt weiterzuentwickeln?',
  'Welche organisationalen Ressourcen empfinden Sie aktuell als besonders unterstützend in Ihrer Arbeit?',
  'Welche organisationalen Ressourcen fehlen Ihnen — und an wen könnten Sie sich wenden, um daran etwas zu ändern?',
  'Welche Wechselwirkung erleben Sie zwischen personalen und organisationalen Ressourcen?',
  'Wenn Sie in 12 Monaten auf diesen Bericht zurückblicken — woran würden Sie merken, dass Sie an einer Ressource gearbeitet haben?',
]

function Section({ number, id, title, children }) {
  return (
    <section
      id={id}
      className="card p-8 scroll-mt-24 print:break-before-page first:print:break-before-auto"
    >
      <div className="flex items-baseline gap-3 mb-5">
        <span className="text-2xl font-bold text-teal-700 leading-none">{number}</span>
        <h2 className="text-xl font-bold uppercase tracking-wide text-teal-800">{title}</h2>
      </div>
      {children}
    </section>
  )
}

function Stat({ label, value, suffix }) {
  return (
    <div className="rounded-xl border border-teal-100 bg-teal-50/30 p-4">
      <div className="text-xs uppercase tracking-wider text-ink-soft">{label}</div>
      <div className="text-2xl font-bold text-ink mt-1">
        {value}
        {suffix && <span className="text-base text-ink-soft font-normal ml-1">{suffix}</span>}
      </div>
    </div>
  )
}

function ScaleList({ scales }) {
  return (
    <div className="space-y-5">
      {scales.map(s => <ScaleRow key={s.scaleKey} scale={s} />)}
    </div>
  )
}

function ScaleRow({ scale }) {
  const band = bandFor(scale.average)
  const pct = scale.average != null ? ((scale.average - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100 : 0
  return (
    <div className="grid sm:grid-cols-[1fr_auto] gap-3 sm:gap-6 items-start">
      <div>
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-widest text-teal-700 font-semibold">{scale.id}</span>
          <h3 className="text-base font-semibold">{scale.label}</h3>
        </div>
        {scale.description && (
          <p className="text-sm text-ink-soft leading-snug mt-0.5">{scale.description}</p>
        )}
        <div className="mt-2 h-2 w-full rounded-full bg-teal-100/60 overflow-hidden">
          <div
            className={`h-full rounded-full ${toneColor(band?.tone)}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      <div className="text-right shrink-0 sm:w-28">
        <div className="text-2xl font-bold text-ink leading-none">
          {scale.average != null ? scale.average.toFixed(1) : '—'}
          <span className="text-sm text-ink-soft font-normal ml-0.5">/{SCALE_MAX}</span>
        </div>
        {band && <div className={`text-[11px] uppercase tracking-wider mt-1 ${toneText(band.tone)}`}>{band.label}</div>}
      </div>
    </div>
  )
}

function toneColor(tone) {
  switch (tone) {
    case 'strong':  return 'bg-emerald-500'
    case 'good':    return 'bg-teal'
    case 'mid':     return 'bg-amber-400'
    case 'low':     return 'bg-orange-400'
    case 'lowest':  return 'bg-rose-500'
    default:        return 'bg-ink-mute'
  }
}
function toneText(tone) {
  switch (tone) {
    case 'strong':  return 'text-emerald-700'
    case 'good':    return 'text-teal-700'
    case 'mid':     return 'text-amber-700'
    case 'low':     return 'text-orange-700'
    case 'lowest':  return 'text-rose-700'
    default:        return 'text-ink-mute'
  }
}

function formatDate(iso) {
  if (!iso) return null
  const d = new Date(iso)
  if (isNaN(d.getTime())) return null
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}
